import React, { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { useLang } from "@/i18n";
import { useCursor } from "@/context/CursorContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Field = ({ id, label, type = "text", value, onChange, textarea }) => (
  <div className="relative border-b border-border py-3 focus-within:border-navy-ink transition-colors duration-300">
    <label htmlFor={id} className="block font-mono text-xs tracking-[0.2em] uppercase text-bone/50 mb-2">
      {label}
    </label>
    {textarea ? (
      <textarea
        id={id}
        data-testid={`contact-${id}`}
        value={value}
        onChange={onChange}
        rows={3}
        required
        className="w-full resize-none bg-transparent font-sans text-lg text-bone placeholder-bone/20 outline-none"
      />
    ) : (
      <input
        id={id}
        data-testid={`contact-${id}`}
        type={type}
        value={value}
        onChange={onChange}
        required
        className="w-full bg-transparent font-sans text-lg text-bone placeholder-bone/20 outline-none"
      />
    )}
  </div>
);

export const Contact = React.forwardRef((props, ref) => {
  const { t } = useLang();
  const { setCursor, reset } = useCursor();
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [loading, setLoading] = useState(false);

  const onChange = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success(t.contact.success);
      setForm({ name: "", email: "", message: "" });
    } catch (err) {
      toast.error(t.contact.error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      ref={ref}
      id="contact"
      data-testid="contact-section"
      className="relative border-t border-border bg-void"
    >
      <div className="mx-auto max-w-[1600px] px-6 md:px-12 py-28 md:py-40">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20">
          <div className="lg:col-span-5">
            <h2 className="font-display text-5xl md:text-7xl font-semibold uppercase tighter leading-[0.9] text-bone">
              {t.contact.title}
            </h2>
            <p className="mt-6 max-w-md font-sans font-light text-bone/60 leading-relaxed">
              {t.contact.intro}
            </p>
          </div>

          <form onSubmit={submit} className="lg:col-span-7 space-y-8" data-testid="contact-form">
            <Field id="name" label={t.contact.name} value={form.name} onChange={onChange("name")} />
            <Field id="email" label={t.contact.email} type="email" value={form.email} onChange={onChange("email")} />
            <Field id="message" label={t.contact.message} value={form.message} onChange={onChange("message")} textarea />

            <motion.button
              type="submit"
              disabled={loading}
              onMouseEnter={() => setCursor("link")}
              onMouseLeave={reset}
              whileTap={{ scale: 0.97 }}
              data-testid="contact-submit"
              className="group relative inline-flex items-center gap-4 overflow-hidden rounded-full bg-bone px-8 py-4 font-mono text-xs tracking-[0.2em] uppercase text-void disabled:opacity-50 transition-colors duration-300"
            >
              <span className="relative z-10 group-hover:text-white transition-colors duration-300">
                {loading ? t.contact.sending : t.contact.send}
              </span>
              <span className="relative z-10 group-hover:text-white transition-colors duration-300">→</span>
              <span className="absolute inset-0 -z-0 translate-y-full bg-navy transition-transform duration-500 group-hover:translate-y-0" />
            </motion.button>
          </form>
        </div>
      </div>
    </section>
  );
});

Contact.displayName = "Contact";
